/**
 * The order diagnosis, turned into words and links (SUPPORT-TOOLS item 1).
 *
 * JSX-free, like the rest of `src/lib`: the dashboard's Jest runs in `node`, so the token → copy
 * maps and the action → link rules are only testable out here.
 *
 * **Verdict tokens ARE translated here** — the one deliberate exception to "API enum tokens render
 * raw" (`docs/architecture/i18n.md` § The ops dashboard, decision D9). A job state is matched
 * against Sentry by an engineer; a verdict is read by a non-technical operator who has to say it to
 * a merchant. The server returns a closed token set (`libs/types` `order-document-verdict.ts`), the
 * generated client carries it as enums, and `diagnosis.spec.ts` fails when an enum gains a token
 * this file has no copy for — the maps below are also typed as total over the enums, so the
 * compiler says it first.
 *
 * Every key is written out as a literal, not built from the token, so the locale parity spec's
 * source scan resolves each one against both catalogues.
 */

import type {
  OpsDiagnosisActionDto,
  OpsDiagnosisCorrectionSubject,
  OpsDiagnosisDocumentOperation,
  OpsDiagnosisDocumentType,
  OpsDiagnosisEscalationKey,
  OpsDiagnosisLookupBasis,
  OpsDiagnosisMerchantActionKey,
  OpsDiagnosisWaitKey,
  OpsOrderCorrectionVerdict,
  OpsOrderInvoiceVerdict,
} from '../../../../libs/api-client/src/generated/models';
import { shopFileBackfillPath, shopFilePath } from './shop-file';

/** Every verdict either family can return, as its string value. */
export type DiagnosisVerdict =
  | `${OpsOrderInvoiceVerdict}`
  | `${OpsOrderCorrectionVerdict}`;

/** Verdict → the one plain-language sentence that leads the tab. */
export const VERDICT_KEYS: Readonly<Record<DiagnosisVerdict, string>> = {
  // A document exists — `{{document}}` names it.
  DOCUMENT_SENT: 'diagnosis.verdict.DOCUMENT_SENT',
  DOCUMENT_PDF_PROCESSING: 'diagnosis.verdict.DOCUMENT_PDF_PROCESSING',
  DOCUMENT_PDF_STUCK: 'diagnosis.verdict.DOCUMENT_PDF_STUCK',
  DOCUMENT_PDF_FAILED: 'diagnosis.verdict.DOCUMENT_PDF_FAILED',
  DOCUMENT_EINVOICE_INCOMPLETE:
    'diagnosis.verdict.DOCUMENT_EINVOICE_INCOMPLETE',
  DOCUMENT_HELD_FOR_MANUAL_SEND:
    'diagnosis.verdict.DOCUMENT_HELD_FOR_MANUAL_SEND',
  DOCUMENT_NO_CUSTOMER_EMAIL: 'diagnosis.verdict.DOCUMENT_NO_CUSTOMER_EMAIL',
  DOCUMENT_EMAIL_IN_PROGRESS: 'diagnosis.verdict.DOCUMENT_EMAIL_IN_PROGRESS',
  DOCUMENT_NOT_EMAILED: 'diagnosis.verdict.DOCUMENT_NOT_EMAILED',
  DOCUMENT_EMAIL_FAILED: 'diagnosis.verdict.DOCUMENT_EMAIL_FAILED',
  // No invoice.
  ORDER_POS_CHANNEL: 'diagnosis.verdict.ORDER_POS_CHANNEL',
  ORDER_BEFORE_INVOICING_ANCHOR:
    'diagnosis.verdict.ORDER_BEFORE_INVOICING_ANCHOR',
  ORDER_NOT_PAID: 'diagnosis.verdict.ORDER_NOT_PAID',
  HOLD_DISMISSED_BY_MERCHANT: 'diagnosis.verdict.HOLD_DISMISSED_BY_MERCHANT',
  HOLD_CLOSED_ORDER_GONE: 'diagnosis.verdict.HOLD_CLOSED_ORDER_GONE',
  HELD_MISSING_RECIPIENT: 'diagnosis.verdict.HELD_MISSING_RECIPIENT',
  HELD_MISSING_CUSTOMER_VAT_ID:
    'diagnosis.verdict.HELD_MISSING_CUSTOMER_VAT_ID',
  HELD_PREINSTALL_DECISION: 'diagnosis.verdict.HELD_PREINSTALL_DECISION',
  HELD_AWAITING_INPUT: 'diagnosis.verdict.HELD_AWAITING_INPUT',
  SHOP_NUMBERING_NOT_CONFIGURED:
    'diagnosis.verdict.SHOP_NUMBERING_NOT_CONFIGURED',
  SHOP_SUBSCRIPTION_INACTIVE: 'diagnosis.verdict.SHOP_SUBSCRIPTION_INACTIVE',
  SHOP_AVV_NOT_ACCEPTED: 'diagnosis.verdict.SHOP_AVV_NOT_ACCEPTED',
  WEBHOOK_IN_PROGRESS: 'diagnosis.verdict.WEBHOOK_IN_PROGRESS',
  WEBHOOK_STUCK: 'diagnosis.verdict.WEBHOOK_STUCK',
  WEBHOOK_FAILED: 'diagnosis.verdict.WEBHOOK_FAILED',
  SKIPPED_REPLAY_MISSED: 'diagnosis.verdict.SKIPPED_REPLAY_MISSED',
  NO_WEBHOOK_EVENT: 'diagnosis.verdict.NO_WEBHOOK_EVENT',
  PAID_WEBHOOK_COMPLETED_WITHOUT_INVOICE:
    'diagnosis.verdict.PAID_WEBHOOK_COMPLETED_WITHOUT_INVOICE',
  HOLD_RESOLVED_INVOICE_MISSING:
    'diagnosis.verdict.HOLD_RESOLVED_INVOICE_MISSING',
  // Shopify is the only witness.
  ORDER_NOT_FOUND: 'diagnosis.verdict.ORDER_NOT_FOUND',
  ORDER_OUTSIDE_READ_WINDOW: 'diagnosis.verdict.ORDER_OUTSIDE_READ_WINDOW',
  SHOPIFY_UNREADABLE: 'diagnosis.verdict.SHOPIFY_UNREADABLE',
  // Refunds, chargebacks, cancellations.
  NO_CORRECTION_EVENTS: 'diagnosis.verdict.NO_CORRECTION_EVENTS',
  REFUND_AMOUNT_REVIEW: 'diagnosis.verdict.REFUND_AMOUNT_REVIEW',
  REFUND_MISSING_INVOICE: 'diagnosis.verdict.REFUND_MISSING_INVOICE',
  REFUND_WEBHOOK_IN_PROGRESS: 'diagnosis.verdict.REFUND_WEBHOOK_IN_PROGRESS',
  REFUND_WEBHOOK_FAILED: 'diagnosis.verdict.REFUND_WEBHOOK_FAILED',
  REFUND_NO_WEBHOOK_EVENT: 'diagnosis.verdict.REFUND_NO_WEBHOOK_EVENT',
  REFUND_NO_MONEY_MOVED: 'diagnosis.verdict.REFUND_NO_MONEY_MOVED',
  REFUND_NOT_OWED_POS: 'diagnosis.verdict.REFUND_NOT_OWED_POS',
  REFUND_NOT_OWED_NO_INVOICING:
    'diagnosis.verdict.REFUND_NOT_OWED_NO_INVOICING',
  REFUND_BLOCKED_SHOP_INELIGIBLE:
    'diagnosis.verdict.REFUND_BLOCKED_SHOP_INELIGIBLE',
  REFUND_INVOICE_NOT_SENT: 'diagnosis.verdict.REFUND_INVOICE_NOT_SENT',
  REFUND_ON_UNPAID_INVOICE: 'diagnosis.verdict.REFUND_ON_UNPAID_INVOICE',
  REFUND_UNEXPLAINED: 'diagnosis.verdict.REFUND_UNEXPLAINED',
  DISPUTE_REVIEW_OPEN: 'diagnosis.verdict.DISPUTE_REVIEW_OPEN',
  CANCELLATION_WEBHOOK_IN_PROGRESS:
    'diagnosis.verdict.CANCELLATION_WEBHOOK_IN_PROGRESS',
  CANCELLATION_WEBHOOK_FAILED: 'diagnosis.verdict.CANCELLATION_WEBHOOK_FAILED',
  CANCELLATION_NO_WEBHOOK_EVENT:
    'diagnosis.verdict.CANCELLATION_NO_WEBHOOK_EVENT',
  CANCELLATION_NOT_OWED_NO_INVOICE:
    'diagnosis.verdict.CANCELLATION_NOT_OWED_NO_INVOICE',
  CANCELLATION_DEFERRED_TO_REFUND:
    'diagnosis.verdict.CANCELLATION_DEFERRED_TO_REFUND',
  CANCELLATION_INVOICE_NOT_SENT:
    'diagnosis.verdict.CANCELLATION_INVOICE_NOT_SENT',
  CANCELLATION_BLOCKED_SHOP_INELIGIBLE:
    'diagnosis.verdict.CANCELLATION_BLOCKED_SHOP_INELIGIBLE',
  CANCELLATION_UNEXPLAINED: 'diagnosis.verdict.CANCELLATION_UNEXPLAINED',
};

/** The key of a verdict's sentence. */
export function verdictKey(verdict: DiagnosisVerdict): string {
  return VERDICT_KEYS[verdict];
}

/** What the document of a DOCUMENT_* verdict is called in its sentence. */
export const DOCUMENT_NAME_KEYS: Readonly<
  Record<`${OpsDiagnosisDocumentType}` | 'CORRECTION', string>
> = {
  INVOICE: 'diagnosis.document.INVOICE',
  CREDIT_NOTE: 'diagnosis.document.CREDIT_NOTE',
  CANCELLATION_INVOICE: 'diagnosis.document.CANCELLATION_INVOICE',
  // A chargeback's § 17 correction is a credit-note row, but it is NOT a Gutschrift — calling it
  // one on the page would repeat the mislabel `documents.md` warns about.
  CORRECTION: 'diagnosis.document.CORRECTION',
};

/**
 * The name of the document a verdict is about, or null when the verdict is not about one.
 *
 * @param documentType - The verdict's document type.
 * @param subject - For a correction line, what it corrects: a dispute's credit note is a
 *   Rechnungskorrektur, not a Gutschrift.
 */
export function documentNameKey(
  documentType: `${OpsDiagnosisDocumentType}` | null | undefined,
  subject?: `${OpsDiagnosisCorrectionSubject}` | null,
): string | null {
  if (!documentType) return null;
  if (documentType === 'CREDIT_NOTE' && subject === 'DISPUTE') {
    return DOCUMENT_NAME_KEYS.CORRECTION;
  }
  return DOCUMENT_NAME_KEYS[documentType];
}

/** The label of a correction line's subject. */
export const SUBJECT_KEYS: Readonly<
  Record<`${OpsDiagnosisCorrectionSubject}`, string>
> = {
  REFUND: 'diagnosis.subject.REFUND',
  DISPUTE: 'diagnosis.subject.DISPUTE',
  CANCELLATION: 'diagnosis.subject.CANCELLATION',
};

/** What the merchant must do, in one sentence. */
export const MERCHANT_ACTION_KEYS: Readonly<
  Record<`${OpsDiagnosisMerchantActionKey}`, string>
> = {
  SUPPLY_RECIPIENT: 'diagnosis.merchantAction.SUPPLY_RECIPIENT',
  SUPPLY_CUSTOMER_VAT_ID: 'diagnosis.merchantAction.SUPPLY_CUSTOMER_VAT_ID',
  DECIDE_PREINSTALL_ORDER: 'diagnosis.merchantAction.DECIDE_PREINSTALL_ORDER',
  OPEN_NEEDS_ATTENTION: 'diagnosis.merchantAction.OPEN_NEEDS_ATTENTION',
  RETRY_FROM_ATTENTION_ITEM:
    'diagnosis.merchantAction.RETRY_FROM_ATTENTION_ITEM',
  COMPLETE_EINVOICE_DATA: 'diagnosis.merchantAction.COMPLETE_EINVOICE_DATA',
  SEND_HELD_DOCUMENT: 'diagnosis.merchantAction.SEND_HELD_DOCUMENT',
  COMPLETE_NUMBERING_ONBOARDING:
    'diagnosis.merchantAction.COMPLETE_NUMBERING_ONBOARDING',
  ACTIVATE_SUBSCRIPTION: 'diagnosis.merchantAction.ACTIVATE_SUBSCRIPTION',
  ACCEPT_AVV: 'diagnosis.merchantAction.ACCEPT_AVV',
  REVIEW_REFUND_AMOUNT: 'diagnosis.merchantAction.REVIEW_REFUND_AMOUNT',
  SUPPLY_ORIGINAL_INVOICE: 'diagnosis.merchantAction.SUPPLY_ORIGINAL_INVOICE',
  REVIEW_DISPUTE: 'diagnosis.merchantAction.REVIEW_DISPUTE',
};

/**
 * Merchant action → the shipped canned response the operator can send as the nudge (A18: a nudge
 * is a ticket reply built from a snippet). Keys are the `action.*` defaults in
 * `apps/api/src/app/internal-admin/canned-responses.defaults.ts`; `diagnosis.spec.ts` checks every
 * one exists there. `null` where no snippet fits — the sentence above is then all there is.
 *
 * The item-specific ones get their own snippet; every other "open this item" instruction shares the
 * generic one, which tells the merchant where to look without naming a screen we would have to keep
 * in sync.
 */
export const MERCHANT_ACTION_SNIPPETS: Readonly<
  Record<`${OpsDiagnosisMerchantActionKey}`, string | null>
> = {
  SUPPLY_RECIPIENT: 'action.provide-recipient',
  SUPPLY_CUSTOMER_VAT_ID: 'action.provide-customer-vat-id',
  DECIDE_PREINSTALL_ORDER: 'action.open-attention-item',
  OPEN_NEEDS_ATTENTION: 'action.open-attention-item',
  RETRY_FROM_ATTENTION_ITEM: 'action.open-attention-item',
  COMPLETE_EINVOICE_DATA: 'action.complete-e-invoice-data',
  SEND_HELD_DOCUMENT: null,
  COMPLETE_NUMBERING_ONBOARDING: 'action.numbering-not-set-up',
  ACTIVATE_SUBSCRIPTION: 'action.subscription-inactive',
  ACCEPT_AVV: 'action.avv-not-accepted',
  REVIEW_REFUND_AMOUNT: 'action.open-attention-item',
  SUPPLY_ORIGINAL_INVOICE: 'action.open-attention-item',
  REVIEW_DISPUTE: 'action.open-attention-item',
};

/** What a WAIT waits for. */
export const WAIT_KEYS: Readonly<Record<`${OpsDiagnosisWaitKey}`, string>> = {
  WEBHOOK_IN_PROGRESS: 'diagnosis.wait.WEBHOOK_IN_PROGRESS',
  PDF_IN_PROGRESS: 'diagnosis.wait.PDF_IN_PROGRESS',
  EMAIL_IN_PROGRESS: 'diagnosis.wait.EMAIL_IN_PROGRESS',
  AWAITING_PAYMENT: 'diagnosis.wait.AWAITING_PAYMENT',
};

/** Why a case goes to development. */
export const ESCALATION_KEYS: Readonly<
  Record<`${OpsDiagnosisEscalationKey}`, string>
> = {
  ORDER_UNREADABLE: 'diagnosis.escalate.ORDER_UNREADABLE',
  UNEXPLAINED_STATE: 'diagnosis.escalate.UNEXPLAINED_STATE',
  CORRECTION_WEBHOOK_FAILED: 'diagnosis.escalate.CORRECTION_WEBHOOK_FAILED',
  CORRECTION_WEBHOOK_MISSING: 'diagnosis.escalate.CORRECTION_WEBHOOK_MISSING',
  CORRECTION_BLOCKED_AT_THE_TIME:
    'diagnosis.escalate.CORRECTION_BLOCKED_AT_THE_TIME',
  INVOICE_NOT_SENT_AT_CORRECTION:
    'diagnosis.escalate.INVOICE_NOT_SENT_AT_CORRECTION',
};

/** What the document tools will do for an OPEN_DOCUMENT action. */
export const DOCUMENT_OPERATION_KEYS: Readonly<
  Record<`${OpsDiagnosisDocumentOperation}`, string>
> = {
  REGENERATE_PDF: 'diagnosis.documentOperation.REGENERATE_PDF',
  RESEND_EMAIL: 'diagnosis.documentOperation.RESEND_EMAIL',
};

/** How the Shopify read reached its answer — the evidence behind AC-1.6's three verdicts. */
export const LOOKUP_BASIS_KEYS: Readonly<
  Record<`${OpsDiagnosisLookupBasis}`, string>
> = {
  SHOPIFY_RETURNED_ORDER: 'diagnosis.lookupBasis.SHOPIFY_RETURNED_ORDER',
  NUMBER_BELOW_WINDOW_FLOOR: 'diagnosis.lookupBasis.NUMBER_BELOW_WINDOW_FLOOR',
  ORDER_ID_BELOW_WINDOW_FLOOR:
    'diagnosis.lookupBasis.ORDER_ID_BELOW_WINDOW_FLOOR',
  LOCAL_TRACE_OLDER_THAN_WINDOW:
    'diagnosis.lookupBasis.LOCAL_TRACE_OLDER_THAN_WINDOW',
  NOT_IN_WINDOW: 'diagnosis.lookupBasis.NOT_IN_WINDOW',
  NO_ORDERS_IN_WINDOW: 'diagnosis.lookupBasis.NO_ORDERS_IN_WINDOW',
  READ_FAILED: 'diagnosis.lookupBasis.READ_FAILED',
};

/** The tone of a verdict's banner: calm when nothing is owed, loud when someone must act. */
export type VerdictTone = 'success' | 'info' | 'warning' | 'error';

/**
 * The tone for a verdict, by what has to happen next.
 *
 * @param verdict - The verdict.
 * @param kind - Its action's kind.
 */
export function verdictTone(
  verdict: DiagnosisVerdict,
  kind: `${OpsDiagnosisActionDto['kind']}`,
): VerdictTone {
  // Typed as the enum's string VALUES: TS does not count string-literal cases as exhausting a
  // string enum, and the value is what the wire carries anyway.
  switch (kind) {
    case 'ESCALATE':
      return 'error';
    case 'BACKFILL':
    case 'OPEN_DOCUMENT':
    case 'MERCHANT_ACTION':
      return 'warning';
    case 'WAIT':
      return 'info';
    case 'NONE':
      return verdict === 'DOCUMENT_SENT' ? 'success' : 'info';
  }
}

/** What the tab shows for one next action. Keys, links and ids — the component only renders. */
export type ActionView =
  | {
      readonly kind: 'link';
      readonly labelKey: string;
      /** One sentence under the button saying what will happen there. */
      readonly hintKey: string;
      readonly to: string;
    }
  | {
      readonly kind: 'merchant';
      readonly instructionKey: string;
      /** The canned response to show and copy, when one fits. */
      readonly snippetKey: string | null;
      readonly attentionItemType: string | null;
    }
  | { readonly kind: 'wait'; readonly messageKey: string }
  | {
      readonly kind: 'escalate';
      readonly messageKey: string;
      readonly failedJobId: string | null;
    }
  | { readonly kind: 'none' };

/**
 * The one next action as the tab renders it.
 *
 * - BACKFILL → the shop file's one-order backfill tab, prefilled with the order
 *   ({@link shopFileBackfillPath}). The admin backfill, NOT the merchant's "Create invoice": it
 *   dates the invoice on the order's payment date (`processed_at`), where the merchant's retry
 *   stamps today. The tab still asks for a reason and a confirmation — the link only saves typing.
 * - OPEN_DOCUMENT → the shop file's Documents tab, where the regenerate-a-missing-PDF and
 *   resend-the-stored-PDF tools live (D4). The ticket rides along so the Documents tab can offer
 *   the recipient override it requires (A7).
 * - MERCHANT_ACTION → what the merchant must do, plus the matching snippet. Support resolves no
 *   item itself (AC-G1a).
 * - ESCALATE → hand to development, plainly.
 *
 * @param action - The verdict's action.
 * @param context - The shop and the ticket the operator came from.
 */
export function actionView(
  action: OpsDiagnosisActionDto,
  context: {
    readonly shopId: string;
    readonly ticketId: string | null;
  },
): ActionView {
  // String values, for an exhaustive switch — see `verdictTone`.
  const kind: `${OpsDiagnosisActionDto['kind']}` = action.kind;
  switch (kind) {
    case 'BACKFILL':
      return action.orderId
        ? {
            kind: 'link',
            labelKey: 'diagnosis.action.backfillLabel',
            hintKey: 'diagnosis.action.backfillHint',
            to: shopFileBackfillPath(
              context.shopId,
              action.orderId,
              context.ticketId ?? undefined,
            ),
          }
        : {
            kind: 'escalate',
            messageKey: ESCALATION_KEYS.UNEXPLAINED_STATE,
            failedJobId: null,
          };
    case 'OPEN_DOCUMENT':
      return {
        kind: 'link',
        labelKey: 'diagnosis.action.openDocumentLabel',
        hintKey: action.documentOperation
          ? DOCUMENT_OPERATION_KEYS[action.documentOperation]
          : 'diagnosis.action.openDocumentHint',
        to: shopFilePath(
          context.shopId,
          'documents',
          context.ticketId ?? undefined,
        ),
      };
    case 'MERCHANT_ACTION': {
      const key = action.merchantActionKey ?? 'OPEN_NEEDS_ATTENTION';
      return {
        kind: 'merchant',
        instructionKey: MERCHANT_ACTION_KEYS[key],
        snippetKey: MERCHANT_ACTION_SNIPPETS[key],
        attentionItemType: action.attentionItemType ?? null,
      };
    }
    case 'WAIT':
      return {
        kind: 'wait',
        messageKey: WAIT_KEYS[action.waitKey ?? 'WEBHOOK_IN_PROGRESS'],
      };
    case 'ESCALATE':
      return {
        kind: 'escalate',
        messageKey:
          ESCALATION_KEYS[action.escalationKey ?? 'UNEXPLAINED_STATE'],
        failedJobId: action.failedJobId ?? null,
      };
    case 'NONE':
      return { kind: 'none' };
  }
}

/**
 * The snippet body in the language to send it in.
 *
 * The shop's language, because no ticket is guaranteed here; inside a ticket the reply follows the
 * frozen `ticket.locale` (AC-5.2), which is why the operator can switch.
 *
 * @param snippet - The canned response.
 * @param language - `de` or `en`.
 */
export function snippetBody(
  snippet: { readonly bodyDe: string; readonly bodyEn: string },
  language: 'de' | 'en',
): string {
  return language === 'de' ? snippet.bodyDe : snippet.bodyEn;
}

/** The snippet language for a shop locale: German for any `de*` locale, else English. */
export function snippetLanguageFor(
  shopLocale: string | null | undefined,
): 'de' | 'en' {
  return shopLocale?.toLowerCase().startsWith('de') ? 'de' : 'en';
}
